import React, {useState, useMemo} from "react"
import {useLocation, useHistory} from "react-router-dom"
import _ from "lodash"
import moment from "moment"
import {saveAs} from "file-saver"

// UI
import {text} from "@uprise/typography"
import {Card} from "@uprise/card"
import {spacing} from "@uprise/spacing"
import {primary} from "@uprise/colors"
import {Button} from "@uprise/button"

//Icons
import ExportIcon from "assets/images/png/icon-export.png"
import EmailIcon from "assets/images/png/icon-email.png"
import CrossIcon from "assets/images/png/icon-cancel.png"
import ChevronRightIcon from "assets/images/png/icon-chevron-right.png"

// Components
import ComponentWrapper from "components/HOC/ComponentWrapper"
import TableComponent from "components/shared/Table"

// Styles
import {HeaderTableContent} from "features/AdditionalSession/index.style"
import {ActionButtonContainer, ColumnContainer, Link} from "../Employers/index.style"

const UtilisationReports = () => {
	const location = useLocation()
	const history = useHistory()
	const data = location?.state?.data
	const tableHeader = location?.state?.reportsTableHeader

	const [tableRowData, setTableRowData] = useState(data)

	const handleDeleteReport = selectedData => {
		setTableRowData(tableRowData.filter(row => row.code !== selectedData.code))
	}

	const handleDownloadCSV = () => {
		const csvRows = []

		// Create the header row
		const headers = [
			"Employer Name",
			"Employer Code",
			"Period Signups",
			"Total Signups",
			"Signup Percentage",
			"Wellbeing Checks",
			"Skills",
			"Coaching Sessions",
			"Counselling Sessions",
			"Risk Callbacks"
		]
		csvRows.push(headers.join(","))

		// Create the data rows
		tableRowData.forEach(item => {
			const row = [
				item.companyName,
				item.code,
				item.signUps,
				item.totalSignUps,
				item.signUpPercent,
				item.wellbeingChecks,
				item.skills,
				item.bookingsThirty,
				item.bookingsSixty,
				item.rcbCount
			]
			csvRows.push(row.join(","))
		})

		// Combine rows into a single CSV string
		const csvString = csvRows.join("\n")

		// Create a Blob object for the CSV data
		const blob = new Blob([csvString], {type: "text/csv;charset=utf-8"})

		// Save the file using FileSaver.js
		const filename = `Utilisation report - ${moment().format("YYYY-MM-DD HH:mm")}.csv`
		saveAs(blob, filename)
	}

	const columns = useMemo(() => [
		{
			Header: "Employer Name",
			accessor: "companyName",
			show: true,
			Cell: cellInfo => {
				return (
					<ColumnContainer>
						<Link
							fontSize={text.t4}
							target='_blank'
							color={primary.purple}
							onClick={() => {
								window.open(`/employer/${cellInfo.row.original.code}`, "_blank")
							}}
							id={cellInfo.row.original.id}
							row={cellInfo.row.original}>
							{cellInfo.row.original.companyName}
						</Link>
					</ColumnContainer>
				)
			}
		},
		{
			Header: "Employer Code",
			accessor: "code",
			show: true,
			Cell: cellInfo => {
				return (
					<ColumnContainer>
						<Link
							fontSize={text.t4}
							target='_blank'
							color={primary.purple}
							onClick={() => {
								window.open(`/employer/${cellInfo.row.original.code}`, "_blank")
							}}
							id={cellInfo.row.original.id}
							row={cellInfo.row.original}>
							{cellInfo.row.original.code}
						</Link>
					</ColumnContainer>
				)
			}
		},
		{
			Header: "Period Signups/Total Signups",
			accessor: "signUps/totalSignUps",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.signUps}/{cellInfo.row.original.totalSignUps}
				</ColumnContainer>
			)
		},
		{
			Header: "Signup Percentage",
			accessor: "signUpPercent",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.signUpPercent}
				</ColumnContainer>
			)
		},
		{
			Header: "Wellbeing Checks",
			accessor: "wellbeingChecks",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.wellbeingChecks}
				</ColumnContainer>
			)
		},
		{
			Header: "Skills",
			accessor: "skills",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.skills}
				</ColumnContainer>
			)	
		},
		{
			Header: "Coaching Sessions",
			accessor: "bookingsThirty",
			show: true,	
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.bookingsThirty}
				</ColumnContainer>
			)	
		},
		{
			Header: "Counselling Sessions",
			accessor: "bookingsSixty",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.bookingsSixty}
				</ColumnContainer>
			)
		},
		{
			Header: "Risk Callbacks",
			accessor: "rcbCount",
			show: true,
			Cell: cellInfo => (
				<ColumnContainer alignText='center'>
					{cellInfo.row.original.rcbCount}
				</ColumnContainer>
			)	
		},
		{
			Header: "Download",
			accessor: "",
			show: true,
			disableSortBy: true,
			Cell: cellInfo => {
				return (
					<ActionButtonContainer
						data-testid='cancelBookingButton'
						alignText='center'>
						<img style={{height: "25px", width: "25px"}} src={ExportIcon} />
					</ActionButtonContainer>
				)
			}
		},
		{
			Header: "Email",
			accessor: "",
			show: true,
			disableSortBy: true,
			Cell: cellInfo => {
				return (
					<ActionButtonContainer data-testid='cancelBookingButton' alignText='center'>
						<img style={{height: "25px", width: "25px"}} src={EmailIcon} />
					</ActionButtonContainer>
				)
			}
		},
		{
			Header: "Action",
			accessor: "",
			show: true,
			disableSortBy: true,
			Cell: cellInfo => {
				return (
					<ActionButtonContainer
						data-testid='cancelBookingButton'
						alignText='center'
						onClick={() => handleDeleteReport(cellInfo.row.original)}>
						<img src={CrossIcon} />
					</ActionButtonContainer>
				)
			}
		}
	])

	return (
		<ComponentWrapper
			menuActiveIndex={5}
			headerTitle={
				<div>
					<img  style={{ paddingRight: '15px', cursor: 'pointer' }} onClick={() => history.goBack()} src={ChevronRightIcon} />
					{/* <a style={{color: "rgb(125,96,255)", cursor: "pointer", paddingRight: "10px"}} >
						&lt;
					</a> */}
					<span>{`${tableHeader} (${data?.length} ${data?.length > 1 ? "Reports" : "Report"})`}</span>
				</div>
			}
			headerRightAlignContent={
				<HeaderTableContent>
					<Button
						size='small'
						title={"Export as csv"}
						style={{margin: "10px", padding: "20px 24px"}}
						variant='primary'
						fullWidth={false}
						onClick={handleDownloadCSV}
						disabled={!tableRowData.length}
					/>
				</HeaderTableContent>
			}>
			<Card padding={spacing.s10} backgroundColor='white'>
				<TableComponent columns={columns} data={tableRowData} loading={false} />
			</Card>
		</ComponentWrapper>
	)
}

export default UtilisationReports
